<script setup>
import DataTableField from '@/components/form/DataTableField.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import FilterChipGroup from '@/components/boundary-condition/FilterChipGroup.vue'
import DropDownSingleSelect from '@/components/form/DropDownSingleSelect.vue'
import { computed, onMounted, ref, toRaw, toRef, watch } from 'vue'
import { boundaryConditionTableMixin } from '@/mixins/boundaryConditionTableMixin'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { useStore } from 'vuex'

const STORE_NAMESPACE = 'crestLevelBoundaryCondition'

const store = useStore()

const props = defineProps({
  items: { type: Array, required: true },
  years: { type: Array, required: true },
  overtoppingRates: { type: Array, required: true },
  count: { type: Number, require: true },
  editable: { require: false, default: true }
})

const items = toRef(props, 'items')

const { x, y, showMenu, selectedItem, show } = actionMenuMixin()
const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()

const deleteFromState = (item) => {
  store.dispatch('crestLevelBoundaryCondition/deleteFromState', item)
}

const deleteItems = (input) => {
  store.dispatch('crestLevelBoundaryCondition/deleteByReferencePoint', input)
}

const emit = defineEmits(['changePage'])

const {
  itemPerPage,
  referencePointsOptions,
  referencePointsShow,
  activeYears,
  activeYearsId,
  filterYearId,
  borderBottom,
  updateOptions,
  deleteReferencePoint,
  displayFilter
} = boundaryConditionTableMixin(
  STORE_NAMESPACE,
  items,
  selectedItem,
  getDirtyClass,
  deleteItems,
  deleteFromState,
  emit
)

const activeSlope = ref([])
const activeOvertoppingRate = ref([])
const crestLevelColspan = ref(0)
const crestLevelYearHeader = ref([])
const crestLevelOverToppingRateHeader = ref([])

const currentProject = computed(() => store.state.project.currentProject)

const options = computed(() => store.state.crestLevelBoundaryCondition.options)
const loading = computed(() => store.state.crestLevelBoundaryCondition.loading)

const activeSlopeValue = computed(() => {
  return activeSlope.value.map((slope) => slope.value)
})
const activeOvertoppingRateValue = computed(() => {
  return activeOvertoppingRate.value.map((overtoppingRate) => overtoppingRate.value)
})
const activeYearsLength = computed(() => {
  return activeYears.value.length
})
const activeSlopeLength = computed(() => {
  return activeSlope.value.length
})
const activeOvertoppingRateLength = computed(() => {
  return activeOvertoppingRate.value.length
})
watch(activeYearsLength, () => {
  updateCrestLevelHeader()
})
watch(activeSlopeLength, () => {
  updateCrestLevelHeader()
})
watch(activeOvertoppingRateLength, () => {
  updateCrestLevelHeader()
})
watch(
  () => props.overtoppingRates,
  () => {
    activeOvertoppingRate.value = structuredClone(toRaw(props.overtoppingRates)) // TODO this needs to be custom
    updateCrestLevelHeader()
  }
)
watch(
  () => props.years,
  () => {
    activeYears.value = structuredClone(toRaw(props.years))
    updateCrestLevelHeader()
  }
)
const setReferencePoint = (input) => {
  store.dispatch('crestLevelBoundaryCondition/setReferencePoint', input)
}

const setParameter = (input) => {
  store.dispatch('crestLevelBoundaryCondition/setParameter', input)
}

const filterCrestLevel = (item) => {
  let bool =
    activeYearsId.value.includes(item.year) &&
    activeSlopeValue.value.includes(item.slope) &&
    activeOvertoppingRateValue.value.includes(item.overtopping_rate)
  return displayFilter(bool)
}
const updateCrestLevelHeader = async () => {
  if (activeOvertoppingRate.value && activeSlope.value) {
    crestLevelColspan.value = activeOvertoppingRateLength.value * activeYearsLength.value
    let crestLevelHeader = []
    let duplicationAmount = options.value
      ? activeSlopeLength.value * activeOvertoppingRateLength.value
      : 0
    for (let i = 0; i < duplicationAmount; i++) crestLevelHeader.push(...props.years)
    crestLevelYearHeader.value = crestLevelHeader

    crestLevelHeader = []
    duplicationAmount = options.value ? activeSlopeLength.value : 0
    for (let i = 0; i < duplicationAmount; i++)
      crestLevelHeader.push(...activeOvertoppingRate.value)
    crestLevelOverToppingRateHeader.value = crestLevelHeader
  }
}
const createFilters = () => {
  activeYears.value = structuredClone(toRaw(props.years))
  activeSlope.value = structuredClone(toRaw(options.value.slope.choices))
  activeOvertoppingRate.value = structuredClone(toRaw(props.overtoppingRates))
}

defineExpose({
  dirtyItems
})

onMounted(async () => {
  createFilters()
  await updateCrestLevelHeader()
})
</script>

<template>
  <div>
    <div style="justify-content: space-between; display: flex">
      <v-card v-if="years.length" variant="flat">
        <FilterChipGroup :active-items="activeYears" :items="years" parameter="year" />
        <FilterChipGroup
          v-if="options"
          :active-items="activeSlope"
          :items="options.slope ? options.slope.choices : []"
          filter-parameter="value"
          parameter="value"
        />
        <FilterChipGroup
          v-if="overtoppingRates"
          :active-items="activeOvertoppingRate"
          :items="overtoppingRates"
          filter-parameter="value"
          parameter="value"
        />
      </v-card>
      <slot></slot>
    </div>
    <v-row>
      <v-col>
        <v-data-table-server
          :footer-props="{
            disableItemsPerPage: true
          }"
          :items="referencePointsShow"
          :items-length="count ?? 0"
          :items-per-page="itemPerPage"
          class="data-table"
          density="compact"
          hide-no-data
          @update:options="updateOptions"
        >
          <template #headers>
            <tr>
              <th></th>
              <template v-if="options && currentProject">
                <th
                  v-for="slopeLevelChoice in options.slope.choices"
                  :key="slopeLevelChoice.id"
                  :colspan="crestLevelColspan"
                  :style="displayFilter(activeSlopeValue.includes(slopeLevelChoice.value))"
                >
                  <span class="pl-1">
                    Crest level
                    <v-chip label size="x-small">{{ slopeLevelChoice.display_name }} </v-chip>
                  </span>
                </th>
              </template>
            </tr>
            <tr>
              <th></th>
              <th
                v-for="overToppingRate in crestLevelOverToppingRateHeader"
                :key="overToppingRate.id"
                :colspan="activeYearsLength"
                :style="displayFilter(activeOvertoppingRateValue.includes(overToppingRate.value))"
              >
                <span class="pl-1"> {{ overToppingRate.display_name }} </span>
              </th>
            </tr>
            <tr>
              <th :style="`min-width: 200px`">Reference point</th>
              <th
                v-for="(year, index) in crestLevelYearHeader"
                :key="`crest_level-${year.id}-${index}`"
                :style="filterYearId(year.id)"
                class="pl-1 year-header"
              >
                <span class="pl-1"> {{ year.year }} </span>
              </th>
            </tr>
          </template>
          <template #body>
            <tr v-if="loading" class="v-data-table-progress">
              <th colspan="999" style="text-align: center">
                <v-progress-linear color="primary" indeterminate />
              </th>
            </tr>
            <template v-else>
              <tr
                v-for="referencePoint in referencePointsShow"
                :key="referencePoint.id"
                @contextmenu="show($event, referencePoint)"
              >
                <td>
                  <DropDownSingleSelect
                    :class="borderBottom(referencePoint)"
                    :editable="editable"
                    :item="referencePoint.id"
                    :options="referencePointsOptions"
                    @addDirty="addDirty(referencePoint, referencePoint.id)"
                    @update="setReferencePoint"
                  />
                </td>
                <td
                  v-for="item in referencePoint.crestlevelboundarycondition_set"
                  :key="`crestLevel-${item.id}`"
                  :style="filterCrestLevel(item)"
                  class="pa-0"
                >
                  <DataTableField
                    :class="getDirtyClass(item, 'crest_level')"
                    :editable="editable"
                    :item="item"
                    parameter="crest_level"
                    width="75px"
                    @set="setParameter"
                    @update="addDirty(item, 'crest_level', 'crestLevel')"
                  />
                </td>
              </tr>
            </template>
          </template>
        </v-data-table-server>
      </v-col>
    </v-row>
    <TableActionMenu
      :add-button="false"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      @action="deleteReferencePoint()"
      @update:show-menu="showMenu = $event"
    />
  </div>
</template>

<style scoped>
.v-table:deep(th) {
  border-bottom: 1px solid rgba(255, 255, 255, 0) !important;
  text-align: left;
  padding: 0 !important;
  font-size: 0.75rem;
  font-family: Roboto, sans-serif;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}

.v-table:deep(td) {
  padding: 0 !important;
}

.v-table:deep(th) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.year-header {
  min-width: 75px;
}
</style>
